import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';
import { MessageSquare, Send, Share2, MoreVertical, Heart, UserPlus, Check, Clock } from 'lucide-react';
import ReactionButton from './ReactionButton';
import { db } from '../firebase/config';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';

const RenderTextWithMentions = ({ text, navigate }) => {
    if (!text) return null;
    const parts = text.split(/(@[a-z0-9_]+|#[a-z0-9_]+)/gi);

    return (
        <p className="my-2.5 sm:my-3 text-slate-800 dark:text-slate-100 text-sm sm:text-base leading-relaxed whitespace-pre-wrap font-normal">
            {parts.map((part, index) => {
                if (part.startsWith('@')) {
                    const username = part.slice(1);
                    return (
                        <span
                            key={index}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate('search-profile', { username });
                            }}
                            className="text-sky-500 dark:text-sky-400 font-semibold hover:underline cursor-pointer transition-colors"
                        >
                            {part}
                        </span>
                    );
                } else if (part.startsWith('#')) {
                    return (
                        <span
                            key={index}
                            className="text-indigo-500 dark:text-indigo-400 font-medium hover:underline cursor-pointer"
                        >
                            {part}
                        </span>
                    );
                }
                return part;
            })}
        </p>
    );
};

const PostCard = ({ post, navigate, onDelete }) => {
    const {
        currentUser,
        userProfile,
        sendFriendRequest,
        cancelFriendRequest,
        acceptFriendRequest,
        removeFriend,
        blockUser
    } = useAuth();
    const { openModal } = useModal();
    const [menuOpen, setMenuOpen] = useState(false);
    const [heartBurst, setHeartBurst] = useState(false);

    // Life bar countdown logic (24 hours)
    const [timeLeftPercentage, setTimeLeftPercentage] = useState(100);
    const [timeLeftLabel, setTimeLeftLabel] = useState('');

    useEffect(() => {
        if (!post?.createdAt) return;
        const createdAt = post.createdAt.toDate ? post.createdAt.toDate() : new Date(post.createdAt);
        const expiresAt = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000);

        const updateTimer = () => {
            const now = new Date();
            const totalDuration = 24 * 60 * 60 * 1000;
            const remaining = expiresAt - now;

            if (remaining <= 0) {
                setTimeLeftPercentage(0);
                setTimeLeftLabel('Expired');
            } else {
                const percentage = (remaining / totalDuration) * 100;
                setTimeLeftPercentage(percentage);

                const hours = Math.floor(remaining / (1000 * 60 * 60));
                const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                setTimeLeftLabel(`${hours}h ${minutes}m left`);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 60000);
        return () => clearInterval(interval);
    }, [post]);

    if (!post) return null;

    const authorId = post.authorId || post.userId;
    const isAuthor = currentUser && authorId === currentUser.uid;
    const authorProfile = post.authorProfile || {};
    const isFriend = userProfile?.friends?.includes(authorId);
    const hasRequested = userProfile?.outgoingFriendRequests?.includes(authorId);
    const hasIncoming = userProfile?.incomingFriendRequests?.includes(authorId);

    const handleProfileClick = (e) => {
        e.stopPropagation();
        if (authorId) {
            navigate('profile', { userId: authorId });
        }
    };

    const handleDoubleTap = (e) => {
        setHeartBurst(true);
        setTimeout(() => setHeartBurst(false), 800);
    };

    const handleShare = async (e) => {
        e.stopPropagation();
        const shareData = {
            title: `Post by ${authorProfile.name || 'User'} on Batcheet`,
            text: post.text || 'Check out this 24h fleeting thought!',
            url: window.location.href
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // Ignore cancel
            }
        } else {
            await navigator.clipboard.writeText(window.location.href);
            toast.success('Link copied to clipboard! 📋');
        }
    };

    const handleStartChat = async (e) => {
        e.stopPropagation();
        if (!currentUser || !authorId || isAuthor) return;
        const chatId = [currentUser.uid, authorId].sort().join('_');
        const chatRef = doc(db, 'chats', chatId);
        
        await setDoc(chatRef, {
            participants: [currentUser.uid, authorId],
            participantProfiles: {
                [currentUser.uid]: {
                    name: userProfile.displayName,
                    photoURL: userProfile.photoURL,
                    username: userProfile.username
                },
                [authorId]: {
                    name: authorProfile.name,
                    photoURL: authorProfile.photoURL,
                    username: authorProfile.username
                }
            },
            lastUpdated: serverTimestamp(),
            unreadCount: {
                [currentUser.uid]: 0,
                [authorId]: 0
            }
        }, { merge: true });

        navigate('chat', { chatId });
    };

    const handleFriendAction = async (e) => {
        e.stopPropagation();
        if (!currentUser || !authorId || isAuthor) return;

        if (isFriend) {
            // Two-click confirmation modal
            openModal('confirm', {
                title: 'Remove Friend',
                message: `Are you sure you want to remove @${authorProfile.username || 'this user'} from your friends?`,
                confirmText: 'Remove',
                confirmColor: 'red',
                onConfirm: () => removeFriend(authorId)
            });
        } else if (hasIncoming) {
            await acceptFriendRequest(authorId);
        } else if (hasRequested) {
            await cancelFriendRequest(authorId);
        } else {
            await sendFriendRequest(authorId);
        }
    };

    const handleDeletePost = async () => {
        try {
            await deleteDoc(doc(db, 'posts', post.id));
            toast.success("Post deleted.");
            if (typeof onDelete === 'function') {
                onDelete(post.id);
            }
        } catch (e) {
            console.error("Delete post error:", e);
            toast.error("Could not delete post.");
        }
    };

    return (
        <div 
            onDoubleClick={handleDoubleTap}
            className="group relative bg-white dark:bg-[#111827]/80 backdrop-blur-md border border-slate-200/90 dark:border-slate-800/80 rounded-2xl sm:rounded-3xl shadow-xs hover:shadow-md dark:shadow-black/20 hover:border-sky-500/30 dark:hover:border-sky-500/30 transition-all duration-200 overflow-hidden select-none"
        >
            {/* 24-Hour Countdown Lifebar */}
            <div className="w-full h-1 bg-slate-100 dark:bg-slate-800/80">
                <div
                    className={`h-full transition-all duration-1000 ease-linear ${
                        timeLeftPercentage > 50
                            ? 'bg-gradient-to-r from-sky-400 to-indigo-500'
                            : timeLeftPercentage > 20
                            ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                            : 'bg-gradient-to-r from-rose-500 to-red-600'
                    }`}
                    style={{ width: `${timeLeftPercentage}%` }}
                />
            </div>

            {/* Heart Burst Particle Effect */}
            {heartBurst && (
                <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
                    <Heart className="w-20 h-20 text-rose-500 fill-rose-500 animate-heart-burst" />
                </div>
            )}

            {/* Card Body */}
            <div className="p-4 sm:p-5" onClick={() => navigate('post', { postId: post.id })}>
                {/* Header: Author + Actions */}
                <div className="flex items-start justify-between gap-3 mb-1.5 sm:mb-2">
                    <div onClick={handleProfileClick} className="flex items-center gap-2.5 sm:gap-3 cursor-pointer group/author min-w-0">
                        {/* Avatar with subtle border ring */}
                        <div className="relative flex-shrink-0">
                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full p-[1.5px] bg-gradient-to-tr from-sky-400 to-indigo-500 group-hover/author:scale-105 active:scale-95 transition-transform shadow-xs">
                                <img
                                    src={authorProfile.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${post.authorId || post.userId}`}
                                    alt=""
                                    className="w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                                />
                            </div>
                        </div>

                        {/* Name & Handle */}
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-bold text-sm text-slate-900 dark:text-white group-hover/author:text-sky-500 dark:group-hover:text-sky-400 transition-colors truncate">
                                    {authorProfile.name || 'Anonymous'}
                                </span>
                                {post.mood && (
                                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium border border-slate-200/60 dark:border-slate-700/60">
                                        {post.mood}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-mono">
                                <span>@{authorProfile.username || 'user'}</span>
                                <span>•</span>
                                <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                                    <Clock className="w-3 h-3" />
                                    {timeLeftLabel}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right side: Add friend & More Options */}
                    <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {!isAuthor && currentUser && (
                            <button
                                onClick={handleFriendAction}
                                title={
                                    isFriend
                                        ? "Friends (Click to remove)"
                                        : hasIncoming
                                        ? "Accept Friend Request"
                                        : hasRequested
                                        ? "Request Pending (Click to cancel)"
                                        : "Send Friend Request"
                                }
                                className={`p-1.5 sm:p-2 rounded-xl text-xs font-semibold flex items-center gap-1 transition-all ${
                                    isFriend
                                        ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20'
                                        : hasRequested
                                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                                        : hasIncoming
                                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                                        : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                                }`}
                            >
                                {isFriend ? (
                                    <Check className="w-3.5 h-3.5" />
                                ) : hasRequested ? (
                                    <Clock className="w-3.5 h-3.5" />
                                ) : (
                                    <UserPlus className="w-3.5 h-3.5" />
                                )}
                            </button>
                        )}

                        <div className="relative">
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                className="p-1.5 sm:p-2 rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <MoreVertical className="w-4 h-4" />
                            </button>

                            {menuOpen && (
                                <div className="absolute right-0 top-9 z-40 w-40 bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl py-1 animate-slide-up text-xs font-medium">
                                    {isAuthor ? (
                                        <button
                                            onClick={() => {
                                                setMenuOpen(false);
                                                openModal('delete', { onConfirm: handleDeletePost });
                                            }}
                                            className="w-full text-left px-3 py-2 text-rose-500 hover:bg-slate-50 dark:hover:bg-slate-800/80"
                                        >
                                            Delete Post
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    blockUser(post.authorId || post.userId);
                                                }}
                                                className="w-full text-left px-3 py-2 text-rose-500 hover:bg-slate-50 dark:hover:bg-slate-800/80"
                                            >
                                                Block @{authorProfile.username}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    toast.success("Post reported for review.");
                                                }}
                                                className="w-full text-left px-3 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/80"
                                            >
                                                Report Post
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Text */}
                <RenderTextWithMentions text={post.text} navigate={navigate} />

                {/* Optional Image */}
                {post.imageUrl && (
                    <div className="mt-2.5 sm:mt-3 mb-1 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xs">
                        <img
                            src={post.imageUrl}
                            alt="Post attachment"
                            className="w-full h-auto max-h-96 object-cover hover:scale-[1.01] transition-transform duration-300"
                        />
                    </div>
                )}
            </div>

            {/* Actions Bar */}
            <div className="px-4 sm:px-5 py-2.5 sm:py-3 bg-slate-50/70 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between gap-3">
                {/* Reactions */}
                <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-0.5" onClick={(e) => e.stopPropagation()}>
                    <ReactionButton post={post} reactionType="fire" />
                    <ReactionButton post={post} reactionType="funny" />
                    <ReactionButton post={post} reactionType="wholesome" />
                    <ReactionButton post={post} reactionType="mindblown" />
                </div>

                {/* Comment, Message & Share */}
                <div className="flex items-center gap-2.5 sm:gap-3 text-slate-400">
                    <button
                        onClick={() => navigate('post', { postId: post.id })}
                        className="flex items-center gap-1.5 hover:text-sky-500 dark:hover:text-sky-400 transition-colors p-1"
                    >
                        <MessageSquare className="w-4 h-4" />
                        <span className="text-xs font-semibold">{post.commentCount || 0}</span>
                    </button>

                    {!isAuthor && (
                        <button
                            onClick={handleStartChat}
                            title="Send Direct Message"
                            className="hover:text-sky-500 dark:hover:text-sky-400 transition-colors p-1"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    )}

                    <button
                        onClick={handleShare}
                        title="Share Post"
                        className="hover:text-sky-500 dark:hover:text-sky-400 transition-colors p-1"
                    >
                        <Share2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(PostCard);
